'use client'

import { CatalogListWrapperFragment } from '@/components/Catalog/CatalogListWrapper/CatalogListWrapper.generated'
import { CategoryRecord, IndustryRecord } from '@/graphql/types'
import { FilterSelect } from '@desoutter/uikit'
import { z } from 'zod'

const filterValueSchema = z
	.array(z.string())
	.optional()
	.or(
		z
			.string()
			.optional()
			.transform((value) => value.split(',')),
	)
	.default([])

export const filtersSchema = z.object({
	types: filterValueSchema,
	industries: filterValueSchema,
	uses: filterValueSchema,
	categories: filterValueSchema,
	series: filterValueSchema,
})

export type Filters = z.infer<typeof filtersSchema>

type CatalogFiltersProps = {
	fragment: CatalogListWrapperFragment
	filters: Filters
	categories: CategoryRecord[]
	industries: IndustryRecord[]
	onFilterChange: (name: string, value: string) => void
}

export const CatalogFilters = ({
	fragment,
	filters,
	categories,
	industries,
	onFilterChange,
}: CatalogFiltersProps) => {
	return (
		<div className="flex flex-col gap-y-4">
			<FilterSelect
				className="!bg-gray-100 border-0 md:!w-64"
				label={
					filters.types.length ? filters.types.length + ' selected' : 'Types'
				}
				allItemLabel={fragment.allTypesLabel}
				filterName="types"
				filters={filters.types}
				items={[
					{
						label: 'Product',
						value: 'product',
					},
					{
						label: 'Accessory',
						value: 'accessory',
					},
					{
						label: 'Sparepart',
						value: 'sparepart',
					},
					{
						label: 'Service',
						value: 'service',
					},
				]}
				onSelection={onFilterChange}
			/>
			<FilterSelect
				className="!bg-gray-100 border-0 md:!w-64"
				label={
					filters.industries.length
						? filters.industries.length + ' selected'
						: 'Industries'
				}
				allItemLabel={fragment.allIndustriesLabel}
				filterName="industries"
				filters={filters.industries}
				items={industries.map((industry) => ({
					label: industry.label,
					value: industry.value,
				}))}
				onSelection={onFilterChange}
			/>
			<FilterSelect
				className="!bg-gray-100 border-0 md:!w-64"
				label={
					filters.uses.length
						? filters.uses.length + ' selected'
						: fragment.usesLabel
				}
				allItemLabel={fragment.allUsesLabel}
				filterName="uses"
				filters={filters.uses}
				items={[
					{ label: 'To drill', value: 'TO_DRILL' },
					{ label: 'To assemble', value: 'TO_ASSEMBLE' },
					{ label: 'To control', value: 'TO_CONTROL' },
					{ label: 'To remove material', value: 'TO_REMOVEMATERIAL' },
					{ label: 'To optimize', value: 'TO_OPTIMIZE' },
				]}
				onSelection={onFilterChange}
			/>
			<FilterSelect
				className="!bg-gray-100 border-0 md:!w-64"
				label={
					filters.categories.length
						? filters.categories.length + ' selected'
						: fragment.categoriesLabel
				}
				allItemLabel={fragment.allCategoriesLabel}
				filterName="categories"
				filters={filters.categories}
				items={categories.map((category) => ({
					label: category.name,
					value: category.pimId,
				}))}
				onSelection={onFilterChange}
			/>
		</div>
	)
}
